/**
 * HeaderSection - Componente reutilizable adaptado a paleta Slate.
 * * Este componente genera una cabecera de sección consistente que incluye un icono,
 * título, subtítulo opcional y un botón de acción configurable.
 * Soporta modo oscuro automáticamente mediante selectores de atributo Bootstrap.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {string} [props.title='Título'] - Título principal de la sección.
 * @param {string} [props.subtitle=''] - Texto secundario descriptivo debajo del título.
 * @param {string} [props.icon='fas fa-chart-bar'] - Clase de FontAwesome para el icono principal.
 * @param {string|null} [props.buttonText=null] - Texto del botón de acción. Si es null, el botón no se renderiza.
 * @param {string|null} [props.buttonIcon=null] - Clase de FontAwesome para el icono del botón.
 * @param {string} [props.buttonClass='btn btn-primary'] - Clases de CSS/Bootstrap para estilizar el botón.
 * @param {Function|null} [props.onButtonClick=null] - Función callback que se ejecuta al hacer clic en el botón.
 * @param {boolean} [props.compact=false] - Versión de menor altura y margen, para vistas densas.
 * @param {React.ReactNode} [props.children] - Contenido opcional entre el título y el botón (p. ej. KPIs).
 * @returns {JSX.Element} Un contenedor de cabecera con soporte responsivo y estilos integrados.
 */
const HeaderSection = ({
  title = 'Título',
  subtitle = '',
  icon = 'fas fa-chart-bar',
  buttonText = null,
  buttonIcon = null,
  buttonClass = 'btn btn-primary', // Ajustado a primary por defecto
  onButtonClick = null,
  compact = false,
  children = null,
}) => {
  return (
    <div
      className={`header-section-container d-flex justify-content-between align-items-center flex-wrap gap-3 ${compact ? 'is-compact mb-3' : 'mb-5'}`}
    >
      <div className="d-flex align-items-center gap-3">
        {/* Contenedor del Icono - Estilo Slate */}
        <div className="header-icon-box d-flex align-items-center justify-content-center flex-shrink-0">
          <i className={`${icon}`}></i>
        </div>

        <div>
          <h2 className="header-title mb-0 fw-bold">{title}</h2>
          {subtitle && <p className="header-subtitle mb-0">{subtitle}</p>}
        </div>
      </div>

      {children && <div className="header-section-extra">{children}</div>}

      {buttonText && (
        <button
          className={`${buttonClass} header-action-btn d-flex align-items-center gap-2`}
          onClick={onButtonClick}
        >
          {buttonIcon ? (
            <i className={buttonIcon}></i>
          ) : (
            <i className="fas fa-plus"></i> // Icono por defecto más moderno
          )}
          <span>{buttonText}</span>
        </button>
      )}

      <style>{`
        .header-section-container {
          position: relative;
        }

        /* Todo el color sale de los tokens del Design System: así el modo
           oscuro funciona solo y la cabecera combina con el resto del shell. */

        /* Caja de Icono */
        .header-icon-box {
          width: 52px;
          height: 52px;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          border-radius: var(--gm-radius-md);
          color: var(--brand);
          font-size: 1.4rem;
          box-shadow: 0 4px 6px -1px var(--shadow);
        }

        /* Títulos */
        .header-title {
          font-size: 1.5rem;
          color: var(--text);
          letter-spacing: -0.02em;
        }

        .header-subtitle {
          color: var(--text-2);
          font-size: 0.95rem;
        }

        /* Botón de acción */
        .header-action-btn {
          padding: 10px 20px;
          border-radius: var(--gm-radius-md);
          font-weight: 600;
          transition: all 0.2s ease;
          border: none;
        }

        .header-action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 15px -3px rgba(var(--gm-primary-rgb), 0.25);
        }

        /* Contenido intermedio (KPIs u otros) */
        .header-section-extra { flex: 1 1 auto; display: flex; justify-content: center; min-width: 0; }

        /* Variante compacta */
        .is-compact .header-icon-box { width: 40px; height: 40px; font-size: 1.05rem; border-radius: 11px; }
        .is-compact .header-title { font-size: 1.2rem; }
        .is-compact .header-subtitle { font-size: 0.8rem; }
        .is-compact .header-action-btn { padding: 7px 16px; border-radius: 10px; }

        /* Ajuste de responsividad */
        @media (max-width: 576px) {
          .header-title { font-size: 1.25rem; }
          .header-icon-box { width: 44px; height: 44px; font-size: 1.1rem; }
        }
      `}</style>
    </div>
  )
}

export default HeaderSection
